import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Target, Send, MapPin, Trophy, Wind, Dumbbell } from "lucide-react";

/**
 * DailyThrowsBreakdown
 * ────────────────────
 * Replaces the single `throw_volume` field on the player daily check-in.
 * Athletes log per-bucket counts and the component computes the total and
 * surfaces a "mound throws" (bullpen + live game) subtotal — the bucket the
 * workload engine weights heaviest.
 *
 * Wire-up:
 *   <DailyThrowsBreakdown
 *     value={form.throw_breakdown}
 *     onChange={(bd, total) => { setVal("throw_breakdown", bd); setVal("throw_volume", total); }}
 *   />
 *
 * Props:
 *   value      — { catch_play, long_toss, pre_throw_routine, bullpen, live_game, plyo }
 *   onChange   — (nextBreakdown, computedTotal) => void
 *   inputCls   — optional input class string to match the surrounding theme
 */
// `max` mirrors ThrowBreakdown caps in /app/backend/models.py so the form never 422s.
const BUCKETS = [
  { key: "pre_throw_routine", label: "Pre-throw routine",  sub: "J-bands, plyos, band pulldowns", icon: Wind,     color: "rgba(148,163,184,0.85)", max: 200 },
  { key: "catch_play",        label: "Catch play",         sub: "Warm-up tosses, partner play",   icon: Send,     color: "rgba(56,189,248,0.85)",  max: 500 },
  { key: "long_toss",         label: "Long toss",          sub: "Extended-distance throws",       icon: MapPin,   color: "rgba(245,158,11,0.85)",  max: 500 },
  { key: "plyo",              label: "Plyo / weighted",    sub: "PlyoCare, weighted-ball intent", icon: Dumbbell, color: "rgba(168,85,247,0.85)",  max: 200 },
  { key: "bullpen",           label: "Bullpen pitches",    sub: "Mound work · counts heaviest",   icon: Target,   color: "rgba(217,4,41,0.95)",    max: 300 },
  { key: "live_game",         label: "Live / game pitches", sub: "Live BP or game outing",        icon: Trophy,   color: "rgba(217,4,41,0.95)",    max: 300 },
];

export default function DailyThrowsBreakdown({ value, onChange, inputCls = "" }) {
  const bd = value || { catch_play: 0, long_toss: 0, pre_throw_routine: 0, bullpen: 0, live_game: 0, plyo: 0 };
  const total = useMemo(() => BUCKETS.reduce((acc, b) => acc + (Number(bd[b.key]) || 0), 0), [bd]);
  const mound = (Number(bd.bullpen) || 0) + (Number(bd.live_game) || 0);

  const set = (key, raw) => {
    const cap = BUCKETS.find((b) => b.key === key)?.max ?? 500;
    const v = Math.max(0, Math.min(cap, Math.floor(Number(raw) || 0)));
    const next = { ...bd, [key]: v };
    onChange?.(next, BUCKETS.reduce((acc, b) => acc + (Number(next[b.key]) || 0), 0));
  };

  return (
    <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-4" data-testid="daily-throws-breakdown">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)]">Daily Throw Log</div>
          <div className="font-display font-bold text-base text-white mt-0.5">Count today&apos;s throws by bucket</div>
        </div>
        <div className="text-right">
          <div className="font-display font-black text-3xl text-white font-mono-num" data-testid="throws-total">{total}</div>
          <div className="text-[10px] uppercase tracking-wider text-white/45">Total · {mound} mound</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {BUCKETS.map((b) => {
          const Icon = b.icon;
          const v = Number(bd[b.key]) || 0;
          return (
            <div key={b.key}
                 className={`rounded-lg p-3 border transition-colors
                             ${v > 0 ? "border-[var(--brand-secondary)]/40 bg-[var(--brand-secondary)]/5" : "border-white/10 bg-black/30"}`}
                 data-testid={`throw-bucket-${b.key}`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Icon className="h-3.5 w-3.5" style={{ color: b.color }} />
                <div className="text-[11px] font-bold text-white leading-tight">{b.label}</div>
              </div>
              <div className="text-[10px] text-white/45 mb-1.5 leading-tight">{b.sub}</div>
              <Input
                type="number" inputMode="numeric" min={0} max={b.max} step={1} placeholder="0"
                value={v === 0 ? "" : v} onChange={(e) => set(b.key, e.target.value)}
                onFocus={(e) => e.target.select()}
                className={`h-11 text-lg font-mono-num text-center ${inputCls}`}
                data-testid={`throw-input-${b.key}`}
              />
            </div>
          );
        })}
      </div>

      <div className="mt-3 text-[10px] text-white/40 text-center">
        Mound throws (bullpen + live game) feed the Acute:Chronic engine at <b className="text-white/60">1.5×</b> weight.
      </div>
    </Card>
  );
}
